import React, { useEffect, useState } from "react";
import API from "../../services/api";
import EditUserForm from "../../components/User/EditUserForm";
import { t } from "@lingui/macro";

const Users = ({ currentUserEmail, isManager }) => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "email", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const usersPerPage = 10;

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, sortConfig, currentPage]);

  const fetchUsers = async () => {
    try {
      const response = await API.get("/api/users/search", {
        params: {
          query: searchTerm,
          page: currentPage,
          size: usersPerPage,
          sortField: sortConfig.key,
          sortDirection: sortConfig.direction,
        },
      });
      setUsers(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? "▲" : "▼";
    }
    return "";
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEditClick = (user) => {
    setEditingUser(user);
  };

  const handleSave = async (updatedUser) => {
    try {
      await API.put(`/api/users/`, updatedUser);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.email === updatedUser.email ? { ...user, ...updatedUser } : user
        )
      );
      setEditingUser(null);
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleCancel = () => {
    setEditingUser(null);
  };

  const handleDelete = async (email) => {
    try {
      await API.delete(`/api/users/${email}`);
      setUsers((prevUsers) => prevUsers.filter((user) => user.email !== email));
      setEditingUser(null);
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  return (
    <div>
      <h2>{t`Manage Users`}</h2>
      <p>{t`Search, sort, and manage the list of users.`}</p>

      <div style={{ marginBottom: "20px" }}>
        <input
          type="text"
          placeholder={t`Search by email, name, or other fields...`}
          value={searchTerm}
          onChange={handleSearchChange}
          className="search-input"
        />
      </div>

      {editingUser ? (
        <EditUserForm
          user={editingUser}
          onSave={handleSave}
          onCancel={handleCancel}
          onDelete={handleDelete}
          currentUserEmail={currentUserEmail}
          isManager={isManager}
        />
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort("email")} className="sortable">
                  {t`Email`} {getSortIcon("email")}
                </th>
                <th onClick={() => handleSort("firstName")} className="sortable">
                  {t`First Name`} {getSortIcon("firstName")}
                </th>
                <th onClick={() => handleSort("lastName")} className="sortable">
                  {t`Last Name`} {getSortIcon("lastName")}
                </th>
                <th onClick={() => handleSort("phoneNumber")} className="sortable">
                  {t`Phone Number`} {getSortIcon("phoneNumber")}
                </th>
                <th onClick={() => handleSort("role")} className="sortable">
                  {t`Role`} {getSortIcon("role")}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, index) => (
                <tr
                  key={user.email}
                  className={index % 2 === 0 ? "even-row" : "odd-row"}
                >
                  <td>
                    <button
                      onClick={() => handleEditClick(user)}
                      className="link-button"
                    >
                      {user.email}
                    </button>
                  </td>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>{user.phoneNumber}</td>
                  <td>{user.role}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="pagination">
            {Array.from({ length: totalPages }, (_, index) => (
              <button
                key={index}
                onClick={() => paginate(index)}
                className={currentPage === index ? "active-page" : ""}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Users;
